import React, { useContext } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from '@material-ui/core';
import { IoCloseSharp } from 'react-icons/io5';
import { Theme } from '@material-ui/core/styles';

import { DeviceContext } from '@vyce/core/src/contexts/deviceContext';
import { MAIN_CONTAINER_ID } from '../../../constants';
import { AppA, AppIconButton, GridCellWithAvatar } from '../../../components';
import { ButtonTitleWithLoading } from '../../../components/ButtonTitleWithLoading';
import { ControlledTagAutocomplete } from '../../../components/controlled-inputs';

interface Props {
  staff: {
    uuid: string;
    name: string;
    avatar?: string;
    ni_number?: string;
  }[];
  open: boolean;
  onClose: () => void;
  setSignalForRerender: (arg0: string) => void;
  setSubstring: (substring: string) => void;
  onAddManagers: () => Promise<void>;
  teamManagers: {
    uuid: string;
    name: string;
    avatar?: string;
  }[];
  saving?: boolean;
}

export const AddManagerDialog = ({
  open,
  onClose,
  staff,
  onAddManagers,
  setSubstring,
  saving = false,
}: Props) => {
  const { isDesktop, isMobile } = useContext(DeviceContext);
  const theme = useTheme<Theme>();
  return (
    <Dialog
      fullScreen={isMobile}
      container={document.getElementById(MAIN_CONTAINER_ID)}
      open={open}
      maxWidth="lg"
      onClose={onClose}
      aria-labelledby="responsive-dialog-title"
    >
      <DialogTitle id="responsive-dialog-title">Add a Manager</DialogTitle>

      <DialogContent style={{ width: isDesktop ? '680px' : '100%' }}>
        <Typography variant="subtitle1">Select a Manager responsible for this Team.</Typography>
        <Typography variant="caption">
          If the person you're looking for is not in Teams yet, you may invite new Admins in{' '}
          <AppA href="/user-management/admins" content="User Management/ Company Admins"></AppA> tab
        </Typography>
        <Box marginTop={2.5}>
          <ControlledTagAutocomplete
            name="teamManagers"
            margin="none"
            items={staff}
            label="Team Managers"
            setSubstring={setSubstring}
            multiple
            filterSelectedOptions
            renderCustomChip={(item, field, deleteItem) => {
              return (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  border={`1px solid ${theme.palette.divider}`}
                  borderRadius={8}
                  padding="8px 16px"
                  marginTop={1}
                >
                  <GridCellWithAvatar avatarUrl={item?.avatar} name={item?.name ?? ''} />
                  <Box display="flex" alignItems="center">
                    <Box width="160px">
                      <Typography>{item?.ni_number}</Typography>
                    </Box>
                    <AppIconButton
                      variant="default"
                      disableFocusRipple
                      disableRipple
                      disableTouchRipple
                      isSmall
                      onClick={() => deleteItem(field, item.uuid)}
                    >
                      <IoCloseSharp color={theme.palette.text.secondary} size="16px" />
                    </AppIconButton>
                  </Box>
                </Box>
              );
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button size="large" variant="outlined" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          color="primary"
          size="large"
          style={{ width: 100 }}
          cy-test-id="add-existing-user-to-team-button"
          onClick={() => {
            onAddManagers();
          }}
          variant="contained"
          disabled={saving}
        >
          <ButtonTitleWithLoading loading={saving} title="Save" loaderVariant="paper" />
        </Button>
      </DialogActions>
    </Dialog>
  );
};
